import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight, ArrowLeft, User, CreditCard, Loader2, AlertCircle, MessageCircle, FileCheck } from 'lucide-react';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSCSApplicationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [applicationId, setApplicationId] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    niNumber: '',
    phone: '',
    email: '',
    postcode: '',
    address: '',
    city: '',
    serviceType: '',
    cardType: ''
  });

  const cardTypes = [
    { value: 'green-labourer', label: 'Green: Labourer', color: 'bg-green-500' },
    { value: 'red-apprentice', label: 'Red: Apprentice', color: 'bg-red-500' },
    { value: 'red-experienced', label: 'Red: Experienced Worker', color: 'bg-red-600' },
    { value: 'blue-skilled', label: 'Blue: Skilled Worker', color: 'bg-blue-500' },
    { value: 'gold-skilled', label: 'Gold: Skilled Worker', color: 'bg-yellow-500' },
    { value: 'gold-supervisor', label: 'Gold: Supervisor', color: 'bg-yellow-600' },
    { value: 'black-manager', label: 'Black: Manager', color: 'bg-gray-900' },
    { value: 'white-pqp', label: 'White: Professionally Qualified Person', color: 'bg-gray-200' },
    { value: 'white-aqp', label: 'White: Academically Qualified Person', color: 'bg-gray-300' }
  ];

  const serviceTypes = [
    { value: 'new', label: 'New Card Application' },
    { value: 'renew', label: 'Renew Existing Card' },
    { value: 'replace', label: 'Replace Lost/Damaged Card' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatNINumber = (value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    let formatted = '';
    for (let i = 0; i < cleaned.length && i < 9; i++) {
      if (i === 2 || i === 4 || i === 6 || i === 8) {
        formatted += ' ';
      }
      formatted += cleaned[i];
    }
    return formatted;
  };

  const handleNIChange = (e) => {
    const formatted = formatNINumber(e.target.value);
    setFormData(prev => ({ ...prev, niNumber: formatted }));
    if (errors.niNumber) {
      setErrors(prev => ({ ...prev, niNumber: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    
    if (!formData.niNumber.trim()) {
      newErrors.niNumber = 'National Insurance number is required';
    } else if (formData.niNumber.replace(/\s/g, '').length !== 9) {
      newErrors.niNumber = 'Invalid NI number format';
    }
    
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';
    if (!formData.cardType) newErrors.cardType = 'Please select a card type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      createApplicationAndProceed();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createApplicationAndProceed = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/applications/cscs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create application');
      }

      const application = await response.json();
      setApplicationId(application.id);
      setCurrentStep(3);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToPayment = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/applications/cscs/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCard = cardTypes.find(c => c.value === formData.cardType);
  const selectedService = serviceTypes.find(s => s.value === formData.serviceType);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-construction-darkblue text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Apply for CSCS Card</h1>
          <p className="text-gray-300">Complete your application in 3 simple steps</p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-4 md:gap-8">
            {[
              { num: 1, label: 'Personal Details', icon: User },
              { num: 2, label: 'Card Selection', icon: FileCheck },
              { num: 3, label: 'Payment', icon: CreditCard }
            ].map((step, index) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center gap-2 ${currentStep >= step.num ? 'text-construction-darkblue' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                    currentStep > step.num ? 'bg-green-500 text-white' :
                    currentStep === step.num ? 'bg-construction-yellow text-construction-darkblue' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.num ? <CheckCircle size={20} /> : step.num}
                  </div>
                  <span className="hidden md:block font-medium">{step.label}</span>
                </div>
                {index < 2 && (
                  <div className={`w-12 md:w-24 h-1 rounded ${currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          
          {/* Step 1: Personal Details */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <User className="text-construction-yellow" />
                Personal Details
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="John"
                      data-testid="input-first-name"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Smith"
                      data-testid="input-last-name"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">National Insurance Number *</label>
                  <input
                    type="text"
                    name="niNumber"
                    value={formData.niNumber}
                    onChange={handleNIChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.niNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="QQ 12 34 56 C"
                    maxLength={13}
                    data-testid="input-ni"
                  />
                  {errors.niNumber && <p className="text-red-500 text-sm mt-1">{errors.niNumber}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="07123 456789"
                      data-testid="input-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Postcode *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.postcode ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="SW1A 1AA"
                        data-testid="input-postcode"
                      />
                    </div>
                    {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                    <a 
                      href="https://www.royalmail.com/find-a-postcode" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-construction-yellow hover:underline mt-1 inline-block"
                    >
                      Find your postcode
                    </a>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="London"
                      data-testid="input-city"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow resize-none ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="123 High Street, Apartment 4B"
                    data-testid="input-address"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all"
                  data-testid="btn-next-step1"
                >
                  Next Step
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Card Selection */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <FileCheck className="text-construction-yellow" />
                Card Selection
              </h2>

              <div className="space-y-6">
                {/* Service Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Service Type *</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {serviceTypes.map((service) => (
                      <label
                        key={service.value}
                        className={`block p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                          formData.serviceType === service.value
                            ? 'border-construction-yellow bg-construction-yellow/10'
                            : 'border-gray-200 hover:border-construction-yellow/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="serviceType"
                          value={service.value}
                          checked={formData.serviceType === service.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="font-medium text-construction-darkblue">{service.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.serviceType && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle size={16} />
                      {errors.serviceType}
                    </p>
                  )}
                </div>

                {/* Card Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">CSCS Card Type *</label>
                  <div className="space-y-2">
                    {cardTypes.map((card) => (
                      <label
                        key={card.value}
                        className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          formData.cardType === card.value
                            ? 'border-construction-yellow bg-construction-yellow/10'
                            : 'border-gray-200 hover:border-construction-yellow/50'
                        }`}
                        data-testid={`card-type-${card.value}`}
                      >
                        <input
                          type="radio"
                          name="cardType"
                          value={card.value}
                          checked={formData.cardType === card.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`w-8 h-5 rounded ${card.color}`}></div>
                        <span className="font-medium text-construction-darkblue">{card.label}</span>
                        {formData.cardType === card.value && (
                          <CheckCircle className="ml-auto text-green-500" size={20} />
                        )}
                      </label>
                    ))}
                  </div>
                  {errors.cardType && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle size={16} />
                      {errors.cardType}
                    </p>
                  )}
                </div>
              </div>

              {errors.submit && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}

              <div className="mt-8 flex justify-between">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  <ArrowLeft size={20} />
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all disabled:opacity-50"
                  data-testid="btn-next-step2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <CreditCard className="text-construction-yellow" />
                Payment
              </h2>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-lg text-construction-darkblue mb-4">Application Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Applicant:</span>
                    <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{formData.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Card Type:</span>
                    <div className="flex items-center gap-2">
                      {selectedCard && <div className={`w-6 h-4 rounded ${selectedCard.color}`}></div>}
                      <span className="font-medium">{selectedCard?.label}</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium">{selectedService?.label}</span>
                  </div>
                  <hr className="my-4" />
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-green-600">£65.00</span>
                  </div>
                </div>
              </div>

              {/* What's Included */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-green-800 mb-3">What's Included:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    CSCS Card Application Processing
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Document Verification
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Fast-Track Service
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Full Support Until Card Issued
                  </li>
                </ul>
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <button
                  onClick={proceedToPayment}
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all disabled:opacity-50"
                  data-testid="btn-pay"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={24} />
                      Redirecting to Payment...
                    </>
                  ) : (
                    <>
                      <CreditCard size={24} />
                      Pay £65.00 Now
                    </>
                  )}
                </button>

                <button
                  onClick={handleBack}
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  <ArrowLeft size={20} />
                  Back
                </button>
              </div>

              <p className="text-center text-sm text-gray-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Need help with your application?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-6 py-3 rounded-lg font-bold hover:bg-black transition-all"
              >
                <Phone size={20} />
                {companyInfo.phone}
              </a>
              <a
                href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I need help with my CSCS card application`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 transition-all"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSCSApplicationPage;
